import {CONTAINS, CURRENCY, DATE, DOES_NOT_CONTAIN, NUMBER, STRING} from "../constants";
import {isEmpty} from "weed-js";
import {ColumnDef} from "./ColumnDef";


export class Filter {
    constructor(colDef, filterState) {
        this.columnDef = colDef;
        this.filterState = filterState;
    }

    toJSON() {
        return {
            columnDef: this.columnDef ? this.columnDef.toJSON() : null,
            filterState: this.filterState,
        };
    }

    static fromJSON(json) {
        if (!json) return null;
        const colDef = json.columnDef ? ColumnDef.fromJSON(json.columnDef) : null;
        return new Filter(colDef, json.filterState);
    }

    getCanonicalDate(date) {
        if (!date) return "";

        // Convert date to ISO string and remove time part
        try {
            return (new Date(date)).toLocaleDateString();
        } catch (err) {
            console.error("Invalid date format for canonicalization:", date, err);
            return this.getCanonicalText(date); // Return null if date is invalid
        }
    }

    getCanonicalNumber(number) {
        if (isEmpty(number) && number !== 0) return null;

        // Remove non-numeric characters and convert to float
        const numericValue = parseFloat(
            number
                .toString()
                .replace(/[^0-9.-]+/g, "")
        );
        return isNaN(numericValue) ? null : numericValue;
    }

    getCanonicalText(text) {
        if (text === null) {
            return null;
        }
        if (!(text instanceof String) && typeof text !== "string") {
            text = text.toString();
        }

        const canonicalized = text
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/[^a-z0-9]/gi, "");

        if (!canonicalized) return null;

        return canonicalized;
    }

    canonicalize(rawValue) {
        if (this.columnDef.type === CURRENCY || this.columnDef.type === NUMBER) {
            return this.getCanonicalNumber(rawValue); // Extract numeric value from currency string
        } else if (this.columnDef.type === DATE) {
            return this.getCanonicalDate(rawValue); // Convert date string to canonical format
        }
        return this.getCanonicalText(rawValue); // Normalize string for consistent filtering
    }

    filterRowsContainingOrNotContaining(cell) {
        const cellValue = this.getCanonicalText(cell.getDOMText());
        const searchValue = this.getCanonicalText(this.filterState.operand);

        if (cellValue === null) {
            return false;
        }
        if (isEmpty(searchValue)) {
            return true;
        }

        if (this.filterState.operator === CONTAINS) {
            return cellValue.includes(searchValue) || searchValue.includes(cellValue);
        } else if (this.filterState.operator === DOES_NOT_CONTAIN) {
            return !cellValue.includes(searchValue);
        }

        return true; // No specific operator, include all values
    }

    filterBetween(start, end, cell) {
        const cellValue = this.canonicalize(cell.getDOMText());
        const startValue = this.canonicalize(start);
        const endValue = this.canonicalize(end);
        if (startValue !== null && endValue !== null) {
            return cellValue >= startValue && cellValue <= endValue;
        } else if (startValue !== null) {
            return cellValue >= startValue;
        } else if (endValue !== null) {
            return cellValue <= endValue;
        }
        return true; // No specific range, include all values
    }

    filter(cell) {
        // Implement filtering logic based on filterState
        if (isEmpty(this.filterState)) return true;  // No filter applied, include all values

        if (isEmpty(this.columnDef.type)) {
            console.error("Column type is not defined for filtering.", this.columnDef);
            throw new Error("Column type is not defined for filtering." + this.columnDef);
        }

        if (this.filterState.operator === CONTAINS || this.filterState.operator === DOES_NOT_CONTAIN) {
            return this.filterRowsContainingOrNotContaining(cell);
        }

        if (!isEmpty(this.filterState.between?.start) || !isEmpty(this.filterState.between?.end)) {
            const {start, end} = this.filterState.between;
            return this.filterBetween(start, end, cell);
        }

        // no filters applied, keep the row.
        return true;
    }
}


export default Filter;